// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './CompressionDeliveryScanModal.module.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { handleError } from 'util/errorHandler';
import { createAxios } from 'ts/createAxios';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';
import { getApiPath, normalizeNumber } from 'util/commons';
import BigNumber from 'bignumber.js';
import { CompressionDeliveryQRSScannedData } from 'util/Types';
import { API_ENDPOINTS } from 'util/endPoints';
import BookingScannedTable from '../BookingScannedTable';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { DATE_FORMAT } from 'util/ConstantValues';
import QrBarcodeScanner from 'components/atoms/QrBarcodeScanner';

type InputResourcesScanModalProps = {
  onClose?: () => void;
  onSubmit?: (compressions: { id: string; description: string }[]) => void;
};

const CompressionDeliveryScanModal: React.FC<InputResourcesScanModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [scannedData, setScannedData] = useState<
    Record<string, CompressionDeliveryQRSScannedData>
  >({});
  const [scannedCode, setScannedCode] = useState<string>();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const { isLoading } = useSelector(selectLoading);

  const resources = useMemo(() => Object.values(scannedData), [scannedData]);

  const totalWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) => BigNumber(item.weight).plus(acc),
        BigNumber(0)
      ),
    [resources]
  );

  const totalSelectedWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) =>
          checkedIds.includes(item.qrCode)
            ? BigNumber(item.weight).plus(acc)
            : acc,
        BigNumber(0)
      ),
    [resources, checkedIds]
  );

  const handleScanSuccess = useCallback((decodedText: string) => {
    if (decodedText) {
      setScannedCode(decodedText);
    } else {
      setScannedCode(undefined);
    }
  }, []);

  const getScannedData = async () => {
    if (!scannedCode || isLoading || scannedData[scannedCode]) {
      return;
    }

    try {
      const { data } = await createAxios({
        prefix: 'v2',
      }).get<CompressionDeliveryQRSScannedData>(
        getApiPath(API_ENDPOINTS.COMPRESSION_DETAIL, {
          id: scannedCode,
        })
      );

      setScannedData((prev) => ({
        ...prev,
        [scannedCode]: {
          ...data,
          qrCode: scannedCode,
        },
      }));
      setCheckedIds((prev) => [...prev, scannedCode]);
    } catch (error) {
      handleError(error);
    } finally {
      setScannedCode(undefined);
    }
  };

  useEffect(() => {
    getScannedData();
  }, [scannedCode]);

  const handleSubmit = () => {
    if (!checkedIds.length) return;

    const selectedResources = resources.filter((item) =>
      checkedIds.includes(item.qrCode)
    );

    onSubmit?.(
      selectedResources.map(({ id, materialName }) => ({
        id,
        description: materialName,
      }))
    );
  };

  return (
    <Modal centered className={styles.scanModal}>
      <div className={styles.title}>
        <p>{t('compressions.scan_qr')}</p>
        <IconCloseTablet onClick={onClose} />
      </div>

      <div className={styles.content}>
        <QrBarcodeScanner
          onScanSuccess={handleScanSuccess}
          classNames={{ container: styles.qrCodeRegion, video: styles.scanner }}
        />

        <div className={styles.scannedContent}>
          <div className={styles.scannedContentHeader}>
            <p className={styles.scannedTitle}>
              {t('qr_scanning.scanned_result')}
            </p>
            <p className={styles.scannedInfo}>
              {t('qr_scanning.scanned_total_items', {
                total: normalizeNumber({
                  value: resources.length,
                }),
              })}
            </p>
            <p className={styles.scannedInfo}>
              {t('qr_scanning.scanned_total_weight', {
                weight: normalizeNumber({ value: totalWeight }),
              })}
            </p>
          </div>

          <div className={styles.scannedTableWrapper}>
            <BookingScannedTable
              data={scannedData}
              checkedIds={checkedIds}
              setCheckedIds={setCheckedIds}
              columns={[
                {
                  title: t('common.material_name'),
                  dataIndex: 'materialName',
                  width: '25%',
                },
                {
                  title: t('download.thead_volume_reduction_id'),
                  dataIndex: 'id',
                  width: '30%',
                },
                {
                  title: t('common.date_time'),
                  dataIndex: 'registDatetime',
                  width: '25%',
                  render: ({ registDatetime }) =>
                    getFormatDateWithCurrentTimezone(
                      registDatetime,
                      DATE_FORMAT.slaYMDHm
                    ),
                },
                {
                  title: t('common.weight'),
                  dataIndex: 'weight',
                  width: '15%',
                  render: ({ weight }) =>
                    `${normalizeNumber({
                      value: weight,
                    })}kg`,
                },
              ]}
            />

            <div className={styles.scannedFooter}>
              <p>
                {t('qr_scanning.total_selected_weight', {
                  weight: normalizeNumber({
                    value: totalSelectedWeight,
                  }),
                })}
              </p>

              <button
                className={styles.btnRequest}
                type="button"
                disabled={!checkedIds.length}
                onClick={handleSubmit}
              >
                {t('compressions.button_deliver')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CompressionDeliveryScanModal;
